/**
 * A glove screen, written per display in ProGlove's own terms (see README.md,
 * "Displays"): `markDisplay` as a MARK Display template, `mai` as one of MAI's
 * three screen kinds. Without `mai`, MAI shows a translation of the MARK
 * Display screen (`maiFromMarkDisplay`). Devices without a display get nothing.
 */
export interface ProGloveScreen {
  markDisplay: MarkDisplayScreen;
  mai?: MaiScreen;
}

/* ---------------------------------------------------------------- MARK Display */

/**
 * MARK Display's templates (docs.proglove.com, screen templates): `PG1`–`PG3`
 * one to three fields with header, `PG1A` one field without header, and the
 * notifications `PG1C`/`PG2C` positive, `PG1E`/`PG2E` negative, `PG1I`/`PG2I`
 * neutral (one or two fields).
 */
export type MarkDisplayTemplate = 'PG1' | 'PG1A' | 'PG2' | 'PG3' | 'PG1C' | 'PG1E' | 'PG1I' | 'PG2C' | 'PG2E' | 'PG2I';

export interface MarkDisplayField {
  header?: string;
  text: string;
}

export interface MarkDisplayScreen {
  template: MarkDisplayTemplate;
  /** as many as the template has (one to three), in order */
  fields: MarkDisplayField[];
  /** shown this long, then the glove returns to the previous screen; absent: until replaced */
  durationMs?: number;
}

/* ------------------------------------------------------------------------- MAI */

export type MaiScreen = MaiWorkflowScreen | MaiNotificationScreen | MaiListScreen;

/** MAI's preset field states: focused blue, success green, warning yellow, error red (no custom colors) */
export type MaiFieldState = 'focused' | 'success' | 'warning' | 'error';

export interface MaiField {
  header?: string;
  text: string;
  /** a state icon in the field */
  state?: MaiFieldState;
  /** with `state`: the field filled with the state's color */
  highlighted?: boolean;
}

/**
 * Fields in up to three lines of one or two fields (`PgWork…`); the template
 * follows from that shape. Empty lines are dropped.
 */
export interface MaiWorkflowScreen {
  kind: 'workflow';
  /** a narrow strip on top, at most 2 lines */
  title?: string;
  rows: MaiField[][];
  /** the first line takes 70% of the screen (`PgWork2T3`, `PgWork3T4`) instead of an equal share */
  largeTopRow?: boolean;
  buttons?: MaiButtons;
}

/** A notification (`PgNtfT1`) with the type's icon */
export interface MaiNotificationScreen {
  kind: 'notification';
  type: 'success' | 'warning' | 'error';
  /** at most 30 characters */
  tagline: string;
  message?: string;
  /** shown this long, then the glove returns to the previous screen; absent: until replaced */
  durationMs?: number;
}

/**
 * A list (`PgListT1`), scrollable. Items with an `id` are tappable: a tap is
 * reported to `useProGloveAction` with the id. MAI takes at most ten items;
 * beyond that nine and a "+n more" hint are shown.
 */
export interface MaiListScreen {
  kind: 'list';
  header: string;
  items: MaiListItem[];
  buttons?: MaiButtons;
}

export interface MaiListItem {
  text: string;
  /** shown on the right, e.g. a count */
  trailing?: string;
  /** shown below the text */
  underline?: string;
  /** makes the item tappable; at most 30 characters */
  id?: string;
}

/** MAI's four physical buttons */
export type MaiPhysicalButton = 'front_inside' | 'front_outside' | 'back_inside' | 'back_outside';

/** A physical button; a press is reported to `useProGloveAction` with `id`. */
export interface MaiButton {
  /** at most 30 characters */
  id: string;
  label: string;
  color: 'BLUE' | 'YELLOW' | 'GREEN' | 'RED' | 'ORANGE' | 'CYAN';
}

export type MaiButtons = Partial<Record<MaiPhysicalButton, MaiButton>>;

/* -------------------------------------------------------------------- shared */

/** A command without the envelope fields the provider adds (event_id, time_created, device_serial). */
export interface GloveCommandBody {
  api_version: string;
  event_type: string;
  [key: string]: unknown;
}

const markDisplayNotificationTypes: Partial<Record<MarkDisplayTemplate, MaiNotificationScreen['type']>> = {
  PG1C: 'success',
  PG2C: 'success',
  PG1I: 'warning',
  PG2I: 'warning',
  PG1E: 'error',
  PG2E: 'error',
};

/** the tagline of a notification without text */
const notificationFallbackTaglines: Record<MaiNotificationScreen['type'], string> = {
  success: 'Success',
  warning: 'Warning',
  error: 'Failed',
};

/**
 * The MAI screen for a screen written only for MARK Display: a notification
 * template becomes a notification, the others a workflow with one field per
 * line (fields without header and text are dropped).
 */
export const maiFromMarkDisplay = ({ template, fields, durationMs }: MarkDisplayScreen): MaiScreen => {
  const notificationType = markDisplayNotificationTypes[template];
  if (notificationType !== undefined) {
    const [first, second] = fields;
    return {
      kind: 'notification',
      type: notificationType,
      tagline: first !== undefined && first.text !== '' ? first.text : notificationFallbackTaglines[notificationType],
      ...(second !== undefined && { message: second.text }),
      ...(durationMs !== undefined && { durationMs }),
    };
  }

  return {
    kind: 'workflow',
    rows: fields
      .filter((field) => (field.header ?? '') !== '' || field.text !== '')
      .slice(0, 3)
      .map((field) => [field]),
  };
};

/** the screen MAI shows: its own, or the translation of the MARK Display one */
export const maiScreenOf = (screen: ProGloveScreen): MaiScreen => screen.mai ?? maiFromMarkDisplay(screen.markDisplay);

/**
 * A timed screen is shown on top of the base screen and disappears again (e.g.
 * a short "Success"). It is timed only where both displays show it for a
 * while: a flow's final screen on MAI stays until the next one.
 */
export const isTransientScreen = (screen: ProGloveScreen): boolean => {
  const mai = maiScreenOf(screen);
  return (screen.markDisplay.durationMs ?? 0) > 0 && mai.kind === 'notification' && (mai.durationMs ?? 0) > 0;
};
